import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, History, Loader2, Trash2, Upload } from "lucide-react";
import { fmtDate } from "@/lib/helpers";

const today = () => new Date().toISOString().slice(0, 10);

const downloadTemplate = () => {
  const csv = "Learner name,Email,Course,Date completed,Renewal date,Certificate number,Provider\r\nJane Smith,jane@yoursetting.co.uk,Paediatric First Aid,15/03/2024,15/03/2027,PFA-12345,Previous provider\r\n";
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
  a.download = "previous-training-template.csv";
  a.click();
  URL.revokeObjectURL(a.href);
};

export const ImportHistoryDialog = ({ onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const upload = async () => {
    if (!file) return toast.error("Choose a CSV or Excel file first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/membership/history/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.imported} record(s)${data.learners_created ? `, added ${data.learners_created} new learner(s)` : ""}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="import-history-dialog">
        <DialogHeader><DialogTitle className="font-heading">Import previous training</DialogTitle></DialogHeader>
        <ol className="text-sm text-muted-foreground list-decimal pl-5 space-y-1">
          <li>Download the template and fill one row per course completed.</li>
          <li>Dates as DD/MM/YYYY. Renewal date, certificate number and provider are optional.</li>
          <li>Learners not already on your team are added automatically (uses your seats).</li>
        </ol>
        <Button variant="outline" size="sm" className="self-start" onClick={downloadTemplate} data-testid="import-template-button"><Download className="h-4 w-4 mr-1.5" />Download template</Button>
        <Input type="file" accept=".csv,.xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="import-file-input" />
        {result && (
          <div className="rounded-md border border-border p-3 text-sm" data-testid="import-result">
            <p><b>{result.imported}</b> record(s) imported{result.learners_created ? `, ${result.learners_created} new learner(s) added` : ""}.</p>
            {result.skipped.length > 0 && (
              <div className="mt-2"><p className="text-amber-700 font-medium">{result.skipped.length} row(s) skipped:</p>
                <ul className="text-xs text-muted-foreground mt-1 max-h-40 overflow-y-auto space-y-0.5" data-testid="import-skipped-list">{result.skipped.map((s) => <li key={s.row}>Row {s.row}: {s.reason}</li>)}</ul>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{result ? "Close" : "Cancel"}</Button>
          <Button onClick={upload} disabled={busy || !file} data-testid="import-upload-button">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Import</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PastTrainingList = ({ items, onChanged }) => {
  const del = async (h) => {
    if (!window.confirm(`Remove "${h.course_title}" from previous training?`)) return;
    try { await api.delete(`/membership/history/${h.id}`); onChanged(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!items?.length) return null;
  return (
    <div className="mt-3" data-testid="past-training-list">
      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><History className="h-3.5 w-3.5" />Previous training</p>
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {items.map((h) => (
          <span key={h.id} className="text-[11px] pl-2 pr-1 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 flex items-center gap-1" data-testid={`past-training-${h.id}`}>
            {h.course_title} · {fmtDate(h.completed_on)}{h.provider ? ` · ${h.provider}` : ""}{h.expires_on ? ` · renew ${fmtDate(h.expires_on)}` : ""}
            <button onClick={() => del(h)} title="Remove" data-testid={`past-training-remove-${h.id}`}><Trash2 className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
};

export const PastTrainingDialog = ({ learner, courses, onClose, onSaved }) => {
  const [f, setF] = useState({ course_title: "", course_id: "", completed_on: "", expires_on: "", certificate_number: "", provider: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const match = courses.find((c) => c.title.toLowerCase() === f.course_title.trim().toLowerCase());
  const save = async () => {
    if (!f.course_title.trim() || !f.completed_on) return toast.error("Enter the course name and the date it was completed");
    setSaving(true);
    try {
      await api.post(`/membership/candidates/${learner.id}/history`, {
        course_title: f.course_title.trim(), course_id: match?.id || null, completed_on: f.completed_on,
        expires_on: f.expires_on || null, certificate_number: f.certificate_number || null, provider: f.provider || null,
      });
      toast.success(`Previous training added for ${learner.name}`);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="past-training-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add previous training for {learner.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Record training completed before joining, so it shows in your team overview and reports.</p>
        <div className="space-y-3">
          <div>
            <Label>Course name</Label>
            <Input list="past-course-options" value={f.course_title} onChange={(e) => set("course_title", e.target.value)} placeholder="e.g. Paediatric First Aid" className="mt-1.5" data-testid="past-course-input" />
            <datalist id="past-course-options">{courses.map((c) => <option key={c.id} value={c.title} />)}</datalist>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date completed</Label><Input type="date" max={today()} value={f.completed_on} onChange={(e) => set("completed_on", e.target.value)} className="mt-1.5" data-testid="past-completed-input" /></div>
            <div><Label>Renewal / expiry date <span className="text-muted-foreground font-normal">(optional)</span></Label><Input type="date" value={f.expires_on} onChange={(e) => set("expires_on", e.target.value)} className="mt-1.5" data-testid="past-expires-input" /></div>
          </div>
          {!f.expires_on && match && <p className="text-xs text-muted-foreground">Left blank, the renewal date is worked out from this course's renewal period (if it has one).</p>}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Certificate number <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={f.certificate_number} onChange={(e) => set("certificate_number", e.target.value)} className="mt-1.5" data-testid="past-cert-input" /></div>
            <div><Label>Training provider <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={f.provider} onChange={(e) => set("provider", e.target.value)} className="mt-1.5" data-testid="past-provider-input" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="past-save-button">{saving ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
